import * as Constants from './constants'
import * as Request from '../../request'
import * as Common from '../common'

// API response format
export interface ApiTopicTag {
  tag: string;
  weight: number;
}

export interface ApiTopicResponse {
  code: number;
  source: string;
  tags: ApiTopicTag[];
}

// Our internal format
export interface TopicPreference {
  id: string;
  name: string;
  isActive: boolean;
  weight?: number;
  isNewlyAdded?: boolean;
}

// Helper function to convert API response to our format
export const convertApiResponseToTopics = (
  apiResponse: ApiTopicResponse, 
  activeTopicNames: string[] = [], 
  prefix: string = 'topic',
  useWeightForActive: boolean = true,
  defaultActive: boolean = false,
  filterNegativeWeights: boolean = false
): TopicPreference[] => {
  if (!apiResponse.tags || !Array.isArray(apiResponse.tags)) {
    return [];
  }
  
  return apiResponse.tags
    // Filter out topics with negative weights if requested (for user topics)
    .filter(apiTag => !filterNegativeWeights || apiTag.weight > 0)
    .map((apiTag, index) => ({
      id: `${prefix}-${index}`,
      name: apiTag.tag,
      // Set isActive based on useWeightForActive flag
      isActive: useWeightForActive ? apiTag.weight > 0 : defaultActive,
      weight: apiTag.weight,
    }));
};

export const handleTopicPreferenceChange = (id: string) => ({ type: Constants.UPDATE_TOPIC_PREFERENCE, id })

export const addCustomTopics = (topicNames: string[]) => ({ type: Constants.ADD_CUSTOM_TOPICS, topicNames })

export const fetchUserTopics = (communityIdentifier: Common.CommunityIdentifier, guid: string) => 
  Request.requestActionCreator<Request.RequestOptions>(Constants.FETCH_USER_TOPICS)
  ({
    method: 'GET',
    public: true,
    url: `/public/topic_preference/my_topics/${communityIdentifier}/${guid}`,
  })

export const fetchSuggestedTopics = (communityIdentifier: Common.CommunityIdentifier, guid: string) => 
  Request.requestActionCreator<Request.RequestOptions>(Constants.FETCH_SUGGESTED_TOPICS)
  ({
    method: 'GET',
    public: true,
    url: `/public/topic_preference/suggest_topics/${communityIdentifier}/${guid}`,
  })

export const submitTopicPreferences = (
  communityIdentifier: Common.CommunityIdentifier, 
  guid: string, 
  topics: TopicPreference[]
) => {
  // Create tags with weights: 10 for selected topics, -1 for unselected topics
  const tagsWithWeights = topics.map(topic => ({
    tag: topic.name,
    weight: topic.isActive ? 10 : -1
  }));
  
  return Request.requestActionCreator<Request.RequestOptions<{ tags: Array<{tag: string, weight: number}> }>>(Constants.SUBMIT_TOPIC_PREFERENCES)
  ({
    method: 'POST',
    public: true,
    url: `/topic_preference/save_topics/${communityIdentifier}/${guid}`,
    payload: { tags: tagsWithWeights },
  });
}